import {
  createStyles,
  lighten,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core/styles';
import { ReferralsStore, ReferralWiringInformationStore } from '../stores';
import { observer } from 'mobx-react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Paper, PaperBorderType, PreviewBackdrop } from '@roc/ui';
import { useStore } from '@roc/client-portal-shared/hooks';
import { useBorrowerPortalMediaQueries } from '@roc/feature-app-core';
import { TitleComp } from '../../../feature-borrowers/src/caf/components/titleComp';
import {
  formatDate,
  getPdfBlobUrl,
  isCivicUrl,
} from '@roc/feature-utils';
import SignatureCanvas from 'react-signature-canvas';
import { PublicCreditAuthorizationStore } from 'libs/client-portal-shared/src/app/modules/creditBackground/stores/publicCreditAuthorizationStore';
import { useAppRoutes } from '../../../../apps/client-portal-public/src/app/hooks';
import axios from 'axios';
import jsPDF from 'jspdf';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) => ({
  documentContainer: {
    paddingLeft: '32px',
    paddingRight: '32px',
    position: 'relative',
    background: '#FFF',
    fontSize: 16,
    overFlowX: 'auto',
    '& table': {
      minHeight: '40px',
      width: '100%',
      textAlign: 'left',
      borderCollapse: 'collapse',
      border: '1px solid #eee',
    },
    '& table th': {
      textAlign: 'left',
      fontWeight: 'bold',
      background: '#f4f4f4',
      padding: '6px 24px 6px 16px',
      borderBottom: '1px solid #eee',
    },
    '& table tr:last-child th': {
      borderBottom: 'none',
    },
    '& table td': {
      color: 'black',
      borderBottom: '1px solid #eee',
    },
    '& table tr:last-child td': {
      borderBottom: 'none',
    },
    '@media (max-width: 800px)': {
      fontSize: 8,
      padding: 0,
    },
  },
  documentCard: {
    minWidth: '650px',
    borderRadius: 0,
    borderColor: '#aaa',
  },
  requirementsStepCard: {
    minWidth: 0,
    '& div > p': {
      marginTop: '20px',
      background: lighten(theme.palette.primary.main, 0.8),
    },
  },
  requirementsFooterCard: {
    minWidth: 0,
  },
  documentCardTitle: {
    justifyContent: 'center',
    textAlign: 'center',
    background: lighten(theme.palette.primary.main, 0.5),
    fontWeight: 'bold',
    padding: theme.spacing(0.5),
  },
  logoImgContainer: {
    alignItems: 'center',
    margin: '0',
    padding: '16px 16px 0 16px',
  },
  pdfBodyContent: {
    margin: '16px 0',
    fontSize: 12,
    textAlign: 'justify',
  },
}));

const usePrintModeStyles = makeStyles((theme: Theme) => ({
  documentContainer: {
    fontSize: 12,
    padding: '0 32px',
    '& table th': {
      fontSize: 12,
    },
    '& table td': {
      fontSize: 12,
    },
    '& ul,ol': {
      marginLeft: theme.spacing(2),
    },
    '& h6': {
      fontSize: 12,
    },
  },
}));

const usePrintStyles = makeStyles(() => ({
  print: {
    width: '210mm',
    minHeight: '297mm',
    height: 'auto',
    ['& [data-testid="documentMenuButton"]']: {
      display: 'none !important',
    },
  },
}));

export type ReferralWiringInformationSignatureFormPDFForm = {
  preview?: boolean;
  printMode?: boolean;
  store: ReferralWiringInformationStore;
  signature;
};

export const ReferralWiringInformationSignatureFormPDF = ({
  preview = false,
  printMode = false,
  store,
  signature,
}: ReferralWiringInformationSignatureFormPDFForm) => {
  const theme = useTheme();
  const classes = useStyles();
  const containerRef = useRef(null);

  const [printModeContainerStyle, setPrintModeContainerStyle] = useState<any>();
  const [containerStyle, setContainerStyle] = useState<any>();
  const printModeClasses = usePrintModeStyles();

  const [openDownloanPrintModal, setOpenDownloanPrintModal] = useState(false);
  const printClasses = usePrintStyles();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);

  const values = store.getFormValues();

  const onDownloadPrintPdf = async () => {
    setPdfLoading(true);
    const x = await getPdfBlobUrl(containerRef.current, {
      scale: 4,
      onclone: (doc, el) => (el.className += ' ' + printClasses.print),
      useCORS: true,
    }).then(url => {
      return axios
        .get(url.toString(), { responseType: 'arraybuffer' })
        .then(response => {
          setPdfData(response.data);
          setPdfLoading(false);
          return response.data;
        })
        .catch(error => {
          console.error('Error fetching PDF data:', error);
        });
    });
    return x;
  };

  useEffect(() => {
    const doc = new jsPDF('p', 'mm', 'a4');
    let width: any = doc.internal.pageSize.getWidth();
    let height: any = doc.internal.pageSize.getHeight();
    width = `${Math.floor(width)}mm`;
    height = `${Math.floor(height)}mm`;
    setPrintModeContainerStyle({
      margin: 'auto',
      minWidth: width,
      maxWidth: width,
      height: height,
    });
    setContainerStyle({
      margin: 'auto',
      maxWidth: theme.breakpoints.values.lg,
    });
  }, []);

  const companyAbbreviated = isCivicUrl()
    ? 'CIVIC'
    : 'FACo';
  const companyName = isCivicUrl()
    ? 'CIVIC Financial Services'
    : 'Finance of America Commercial';

  return (
    <Box id="information">
      <div
        ref={containerRef}
        style={printMode ? printModeContainerStyle : containerStyle}
      >
        <Box
          className={clsx(
            classes.documentContainer,
            printMode ? printModeClasses.documentContainer : undefined
          )}
        >
          {preview && <PreviewBackdrop />}
          <Box pt={2} pb={2} textAlign="center">
            <Typography style={{ margin: '16px 0' }} variant={'h5'}>
              STANDING AUTHORIZATION AND WIRE INSTRUCTIONS
            </Typography>
            <Typography
              style={{ margin: '16px 0', textDecoration: 'underline' }}
              variant={'h5'}
            >
              {companyAbbreviated} Refer a Friend Program
            </Typography>

            <Table size="small" aria-label="wiring information data">
              <TableBody>
                <TableRow>
                  <TableCell component="th" scope="row" width={180}>
                    <strong>Bank Name:</strong>
                  </TableCell>
                  <TableCell align="left">
                    {values.bankName}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" width={180}>
                    <strong>Bank Account Name:</strong>
                  </TableCell>
                  <TableCell align="left">
                    {values.accountName}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" width={180}>
                    <strong>Routing Number:</strong>
                  </TableCell>
                  <TableCell align="left">
                    {values.routingNumber}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" width={200}>
                    <strong>Bank Account Number:</strong>
                  </TableCell>
                  <TableCell align="left">
                    {values.accountNumber}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row" width={180}>
                    <strong>Address:</strong>
                  </TableCell>
                  <TableCell align="left">
                    {values.address}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Typography className={classes.pdfBodyContent} variant={'body1'}>
              If the above account is not the undersigned entity’s account, or
              if the above account has an account name/title that is not the
              same as the name of the undersigned entity, on behalf of the
              undersigned entity, I hereby further expressly authorize the wire
              transfer, and deposit of funds, in connection with the {companyAbbreviated} Refer
              a Friend Program into such account by {companyName} ("{companyAbbreviated}") or any agent or affiliate of {companyAbbreviated}, and I
              hereby release {companyAbbreviated} and all {companyAbbreviated} affiliates and agents, and each
              of their respective officers, managers, directors, consultants,
              agents, advisors, and representatives, from all loss, damage,
              liability, cost, or expense arising out of or resulting from the
              deposit of funds into such account.
            </Typography>

            <Typography
              className={classes.pdfBodyContent}
              variant={'body1'}
              style={{ fontWeight: 'bold' }}
            >
              The foregoing authorization and wire instructions shall be
              standing and shall remain in full force and effect until such time
              as they are revoked or amended in writing by the undersigned.
            </Typography>

            <Typography className={classes.pdfBodyContent} variant={'body1'}>
              On behalf of the undersigned entity, I hereby acknowledge,
              represent, and/or warrant as follows:
            </Typography>
            <ul>
              <li>
                <Typography
                  className={classes.pdfBodyContent}
                  variant={'body1'}
                >
                  I understand and acknowledge that neither Lender nor any
                  Lender Party shall be responsible for any information,
                  including any negative findings, which may be contained in a
                  Credit Report. I release Lender, all Lender Parties, including
                  its respective employees, officers and owners, from any and
                  all liability for furnishing such information and conducting
                  such an investigation.
                </Typography>
              </li>

              <li>
                <Typography
                  className={classes.pdfBodyContent}
                  variant={'body1'}
                >
                  I am a duly authorized representative of the undersigned
                  entity and have been duly authorized by such entity to execute
                  this Standing Authorization and Wire Instructions on behalf of
                  such entity.
                </Typography>
              </li>

              <li>
                <Typography
                  className={classes.pdfBodyContent}
                  variant={'body1'}
                >
                  The above wire and account information is accurate and correct
                  and I acknowledge responsibility for any errors resulting from
                  incorrect/inaccurate information provided.
                </Typography>
              </li>
              <li>
                <Typography
                  className={classes.pdfBodyContent}
                  variant={'body1'}
                >
                  I authorize {companyAbbreviated} and any {companyAbbreviated} agent or affiliate to effect
                  wires/transfers/deposits into the above specified account in
                  connection with the {companyAbbreviated} Refer a Friend Program in accordance
                  with the terms and provisions of said Refer a Friend Program.
                </Typography>
              </li>
              <li>
                <Typography
                  className={classes.pdfBodyContent}
                  variant={'body1'}
                >
                  {companyAbbreviated} reserves the right, on behalf of itself and any agent or
                  affiliate, to require additional verification of the above
                  specified account information, whether written or oral, prior
                  to authorizing a wire/transfer/deposit, and such additional
                  verification may delay the execution of a wire.
                </Typography>
              </li>
              <li>
                <Typography
                  className={classes.pdfBodyContent}
                  variant={'body1'}
                >
                  The undersigned entity releases {companyAbbreviated} and any affiliate, and
                  each of their respective officers, managers, directors,
                  consultants, agents, advisors, and representatives, from all
                  loss, damage, liability, cost, or expense (Loss) arising out
                  of or resulting from any action taken by {companyAbbreviated} upon
                  instructions provided under this Standing Authorization and
                  Wire Instructions, unless the Loss arose out of {companyAbbreviated}’s failure
                  to exercise ordinary care, act in good faith, or act in
                  accordance with the wire instructions given hereunder, and (2)
                  agrees to indemnify and hold {companyAbbreviated} and all {companyAbbreviated} agents and
                  affiliates harmless from any Loss arising out of or resulting
                  from any action taken by {companyAbbreviated} or any {companyAbbreviated} agent or affiliate
                  in good faith upon instructions provided under this Standing
                  Authorization and Wire Instructions.
                </Typography>
              </li>
              <li>
                <Typography
                  className={classes.pdfBodyContent}
                  variant={'body1'}
                >
                  {companyAbbreviated} shall not be liable for refusing to execute a wire
                  transfer and deposit of funds instruction based on {companyAbbreviated}’s
                  reasonable belief that such wire is violative of applicable
                  law or could cause {companyAbbreviated} to incur legal liability for following
                  such instructions.
                </Typography>
              </li>
              <li>
                <Typography
                  className={classes.pdfBodyContent}
                  variant={'body1'}
                >
                  {companyAbbreviated} cannot guarantee the length of time it takes for funds to
                  be credited to the above specified account after a wire is
                  initiated.
                </Typography>
              </li>
              <li>
                <Typography
                  className={classes.pdfBodyContent}
                  variant={'body1'}
                >
                  {companyAbbreviated} does not have any influence or responsibility for fees or
                  surcharges imposed by other financial institutions involved in
                  the transfer of funds.
                </Typography>
              </li>
              <li>
                <Typography
                  className={classes.pdfBodyContent}
                  variant={'body1'}
                >
                  THIS STANDING AUTHORIZATION AND WIRE INSTRUCTIONS SHALL BE GOVERNED
                  BY NEW YORK LAW.
                </Typography>
              </li>
            </ul>

            <Box mt={3}>
              <Box mb={0.5} textAlign={'left'}>
                <strong>Signature</strong>
              </Box>
              <Box display={'flex'} flexDirection={'column'} textAlign={'left'}>
                <Box>
                  <img
                    src={signature}
                    height={120}
                    style={{
                      border: '1px solid #eee',
                    }}
                  />
                </Box>
                <Box color={'blue'}>{formatDate(new Date())}</Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </div>
    </Box>
  );
};
